import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, ShieldCheck, Clock, Sparkles, CreditCard, Headphones } from "lucide-react";
import { Button } from "@/components/ui/button";
import ProductCard, { ProductCardData } from "@/components/ProductCard";
import { fetchProductsWithStock } from "@/lib/products";
import { useApp } from "@/hooks/useApp";
import Seo from "@/components/Seo";

// Below-the-fold (testimonials + trust + FAQ) is split into a separate chunk
// loaded after first paint to keep TBT/LCP small.
const HomeBelowFold = lazy(() => import("@/components/HomeBelowFold"));

const faqsPt = [
  { q: "O que é a NickRobotic?", a: "A NickRobotic é um marketplace digital com entrega automática imediata de seguidores Instagram, contas, ativos digitais e serviços para redes sociais. Você compra, paga e recebe na hora, sem precisar falar com ninguém." },
  { q: "Como funciona a entrega automática?", a: "Após a confirmação do pagamento, o sistema entrega o ativo (credenciais, seguidores, curtidas, etc.) automaticamente em segundos. Para serviços de redes sociais, o pedido é enviado ao provedor e processado em minutos." },
  { q: "Quais formas de pagamento vocês aceitam?", a: "Aceitamos PIX (com confirmação automática) e cartão de crédito/débito via Stripe. Você pode também recarregar sua carteira interna e usar o saldo para qualquer compra." },
  { q: "É seguro comprar seguidores Instagram aqui?", a: "Sim. Trabalhamos com provedores reconhecidos no mercado SMM, oferecemos opção de reposição (refill) em vários serviços e nosso sistema é 100% transparente — você acompanha cada pedido em Meus Serviços." },
  { q: "Em quanto tempo recebo meus seguidores ou serviços?", a: "Produtos digitais (contas, licenças) são entregues instantaneamente. Serviços de redes sociais começam em poucos minutos e são concluídos no prazo informado em cada serviço (normalmente em até 24h)." },
  { q: "Vocês oferecem garantia ou reposição?", a: "Sim. Diversos serviços têm refill automático em caso de queda. Para produtos digitais, suas credenciais ficam disponíveis por 30 dias em Meus Pedidos e nosso suporte está pronto para resolver qualquer problema." },
  { q: "Posso fazer pedidos em massa para vários links?", a: "Sim. Cada serviço tem a opção Pedido em Massa, onde você cola até centenas de links (com quantidades opcionais por linha) ou importa um CSV/TXT." },
  { q: "Preciso fornecer minha senha do Instagram?", a: "Não. Para serviços (seguidores, curtidas, views) basta o link público da conta, post ou vídeo — nunca pedimos sua senha." },
];

const faqsEn = [
  { q: "What is NickRobotic?", a: "NickRobotic is a digital marketplace with instant automatic delivery of Instagram followers, accounts, digital assets and social media services. Buy, pay and receive in seconds." },
  { q: "How does automatic delivery work?", a: "Once payment is confirmed, the system delivers the asset (credentials, followers, likes, etc.) automatically in seconds. Social services start within minutes." },
  { q: "Which payment methods do you accept?", a: "PIX (auto-confirmed) and credit/debit card via Stripe. You can also top up your internal wallet and use the balance for any purchase." },
  { q: "Is it safe to buy Instagram followers here?", a: "Yes. We work with reputable SMM providers, many services include refill, and every order is fully traceable in My Services." },
  { q: "How fast will I receive my order?", a: "Digital products (accounts, licenses) are delivered instantly. Social services start within minutes and complete within the time shown for each service (typically within 24h)." },
  { q: "Do you offer guarantee or refill?", a: "Yes. Many services include automatic refill. For digital products, your credentials stay available for 30 days in My Orders, and our support is ready to help." },
  { q: "Can I place mass orders for multiple links?", a: "Yes. Every service has a Mass Order tab where you can paste hundreds of links (with optional per-line quantity) or import a CSV/TXT." },
  { q: "Do I need to share my Instagram password?", a: "No. For social services (followers, likes, views) only the public link is needed — we never ask for your password." },
];

export default function Home() {
  const { t, lang } = useApp();
  const [featured, setFeatured] = useState<ProductCardData[]>([]);
  const [showBelowFold, setShowBelowFold] = useState(false);

  useEffect(() => {
    const w: any = window;
    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      fetchProductsWithStock(3).then((p) => { if (!cancelled) setFeatured(p); }).catch(() => {});
    };
    let idleId: any;
    let timeoutId: any;
    if (typeof w.requestIdleCallback === "function") {
      idleId = w.requestIdleCallback(run, { timeout: 2500 });
    } else {
      timeoutId = setTimeout(run, 1000);
    }
    return () => {
      cancelled = true;
      if (idleId && w.cancelIdleCallback) w.cancelIdleCallback(idleId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  // Defer below-the-fold render (and its data fetches) until the browser is idle,
  // so it never competes with the initial paint. IntersectionObserver gates
  // it on actual scroll proximity (mobile especially); falls back to idle.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (showBelowFold) return;
    const trigger = () => setShowBelowFold(true);
    const w: any = window;
    let io: IntersectionObserver | null = null;
    if (typeof IntersectionObserver !== "undefined" && sentinelRef.current) {
      io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) trigger();
      }, { rootMargin: "600px 0px" });
      io.observe(sentinelRef.current);
    }
    let idleId: any, timeoutId: any;
    if (typeof w.requestIdleCallback === "function") {
      idleId = w.requestIdleCallback(trigger, { timeout: 5000 });
    } else {
      timeoutId = setTimeout(trigger, 3000);
    }
    return () => {
      io?.disconnect();
      if (idleId && w.cancelIdleCallback) w.cancelIdleCallback(idleId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [showBelowFold]);

  const faqs = lang === "en" ? faqsEn : faqsPt;

  const benefits = useMemo(() => [
    { icon: Zap, title: t("bf_instant"), desc: t("bf_instant_d") },
    { icon: ShieldCheck, title: t("bf_secure"), desc: t("bf_secure_d") },
    { icon: Clock, title: t("bf_access"), desc: t("bf_access_d") },
  ], [t]);

  const steps = useMemo(() => (lang === "en"
    ? [
        { icon: Sparkles, title: "Choose your asset", desc: "Browse our catalog of Instagram services, accounts and digital products." },
        { icon: CreditCard, title: "Pay with PIX or card", desc: "Confirmation happens in seconds via PIX or Stripe checkout." },
        { icon: Zap, title: "Receive automatically", desc: "Credentials and orders are delivered instantly to your account." },
        { icon: Headphones, title: "Track and get support", desc: "Follow every order in real time and talk to support whenever you need." },
      ]
    : [
        { icon: Sparkles, title: "Escolha o ativo", desc: "Navegue pelo catálogo de serviços Instagram, contas e produtos digitais." },
        { icon: CreditCard, title: "Pague no PIX ou cartão", desc: "Confirmação em segundos via PIX ou checkout Stripe." },
        { icon: Zap, title: "Receba automaticamente", desc: "Credenciais e pedidos entregues na hora, direto na sua conta." },
        { icon: Headphones, title: "Acompanhe com suporte", desc: "Veja cada pedido em tempo real e fale com o suporte quando precisar." },
      ]), [lang]);

  const faqJsonLd = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  }), [faqs]);

  return (
    <>
      <Seo
        title={lang === "en"
          ? "Buy Instagram Followers, Likes & Real Accounts — Instant Delivery | NickRobotic"
          : "Comprar Seguidores Instagram Brasileiros — Entrega Imediata | NickRobotic"}
        description={lang === "en"
          ? "Buy real Instagram followers, likes and verified accounts with instant automatic delivery. PIX or card, refill guarantee, 24/7 support."
          : "Comprar seguidores Instagram brasileiros e reais com entrega automática em segundos. PIX ou cartão, garantia de reposição e suporte humano 24h."}
        path="/"
        jsonLd={faqJsonLd}
      />

      {/* Hero — pure CSS background (no image request) so the LCP element is the H1 text. */}
      <section
        className="relative overflow-hidden hero-bg"
        style={{ background: "var(--gradient-hero)" }}
      >
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none opacity-60"
          style={{
            background:
              "radial-gradient(60% 50% at 20% 30%, hsl(320 100% 60% / 0.18), transparent 60%), radial-gradient(50% 45% at 80% 70%, hsl(190 100% 55% / 0.16), transparent 60%)",
          }}
        />
        <div className="container relative py-24 md:py-32 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs mb-6">
            <Sparkles className="w-3 h-3" /> {t("hero_badge")}
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-4 max-w-3xl mx-auto leading-tight">
            <span className="gradient-text">{t("hero_title_a")}</span> {t("hero_title_b")}
          </h1>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto mb-8">{t("hero_sub")}</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button asChild size="lg"><Link to="/produtos">{t("hero_cta")} <ArrowRight className="w-4 h-4 ml-1" /></Link></Button>
            <Button asChild size="lg" variant="outline"><Link to="/servicos">{lang === "en" ? "Instagram services" : "Serviços Instagram"}</Link></Button>
          </div>
        </div>
      </section>

      <section className="container py-16">
        <div className="grid md:grid-cols-3 gap-6">
          {benefits.map((b) => (
            <div key={b.title} className="glow-card rounded-xl p-6 border border-border">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{ background: "var(--gradient-primary)" }}>
                <b.icon className="w-6 h-6 text-primary-foreground" />
              </div>
              <h2 className="font-semibold text-lg mb-1">{b.title}</h2>
              <p className="text-sm text-muted-foreground">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="container py-16">
        <div className="max-w-2xl mx-auto text-center mb-10">
          <h2 className="text-3xl font-bold mb-2">{lang === "en" ? "How it works" : "Como funciona a entrega automática"}</h2>
          <p className="text-muted-foreground">
            {lang === "en"
              ? "Four steps from cart to delivery — no waiting, no manual approvals."
              : "Quatro passos do carrinho à entrega — sem espera, sem aprovação manual."}
          </p>
        </div>
        <div className="grid md:grid-cols-4 gap-4">
          {steps.map((s, i) => (
            <div key={s.title} className="glow-card rounded-xl p-5 border border-border relative">
              <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold flex items-center justify-center">{i + 1}</div>
              <s.icon className="w-6 h-6 text-primary mb-3" aria-hidden="true" />
              <h3 className="font-semibold mb-1">{s.title}</h3>
              <p className="text-sm text-muted-foreground">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="container py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="text-3xl font-bold mb-1">{t("featured")}</h2>
            <p className="text-muted-foreground">{t("featured_sub")}</p>
          </div>
          <Button asChild variant="ghost"><Link to="/produtos">{t("see_all")} <ArrowRight className="w-4 h-4 ml-1" /></Link></Button>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {featured.map((p) => <ProductCard key={p.id} p={p} />)}
        </div>
      </section>

      <div ref={sentinelRef} aria-hidden="true" />
      {showBelowFold && (
        <Suspense fallback={null}>
          <HomeBelowFold lang={lang} t={t} faqs={faqs} />
        </Suspense>
      )}
    </>
  );
}
